'use client';

import React, {
  useState, useRef, useLayoutEffect, useEffect,
} from 'react';
import classNames from 'classnames';
import {
  CasesRevealingPoints,
  RevealingPointsWithSubServices,
} from '@sapientpro/sapientpro-data-models/types/components/shared/cases-revealing-points';
import { useAtomValue } from 'jotai';
import { IImage, Video } from '@sapientpro/sapientpro-data-models';
import styles from './RevealingCaseSection.module.scss';
import AnimatedTitle from '../AnimatedTitle';
import { theme } from '../../store/theme';
import RevealingCasePoint from './RevealingCasePoint';
import DynamicHeading from '../DynamicHeading';
import PointMedia from './PointMedia';

type RevealingCaseSectionProps = {
  data: CasesRevealingPoints;
};

const RevealingCaseSection = ({
  data,
}: RevealingCaseSectionProps) => {
  const appTheme = useAtomValue(theme);
  const listRef = useRef<HTMLDivElement>(null);
  const imageLinkRef = useRef<HTMLDivElement>(null);
  const mobileMediaRef = useRef<HTMLDivElement>(null);
  const [activePoint, setActivePoint] = useState<RevealingPointsWithSubServices>(data.revealingPoints[0]);
  const [activePointIndex, setActivePointIndex] = useState<number>(0);
  const [activeMedia, setActiveMedia] = useState<IImage | Video | null>(null);
  const [isVideo, setIsVideo] = useState<boolean>(false);

  const changeActiveMedia = () => {
    const defaultMedia = data.video || data.image;

    const darkMedia = activePoint.darkMedia ?? activePoint.lightMedia;
    const lightMedia = activePoint.lightMedia ?? activePoint.darkMedia;

    const currentMedia = appTheme === 'dark' ? darkMedia : lightMedia;

    const newActiveMedia = currentMedia || defaultMedia;
    const isMediaVideo = newActiveMedia?.ext === '.mp4';

    if (!currentMedia && defaultMedia && activeMedia) return;

    setActiveMedia(newActiveMedia);
    setIsVideo(isMediaVideo);

    if (isMediaVideo) {
      const mobileVideoElement = mobileMediaRef.current?.querySelector('video');
      const videoElement = imageLinkRef.current?.querySelector('video');

      if (mobileVideoElement) mobileVideoElement.src = newActiveMedia.url;
      if (videoElement) videoElement.src = newActiveMedia.url;
    }
  };

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (listRef.current && imageLinkRef.current) {
        imageLinkRef.current.style.height = `${listRef.current.offsetHeight}px`;
      }
    });

    listRef.current && resizeObserver.observe(listRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    changeActiveMedia();
  }, [activePoint, appTheme]);

  return (
    <section className={classNames(styles.revealingCaseSection, 'container')}>
      <div className={styles.revealingCaseHead}>
        {data.title && (
          <DynamicHeading
            headingTag={data.headerLevel || 'h3'}
            className={classNames(styles.revealingCaseSection__title, 'header3')}
          >
            <AnimatedTitle title={data.title} />
          </DynamicHeading>
        )}
        {data.description && <p className={classNames(styles.description, 'bodyLarge')}>{data.description}</p>}
      </div>
      <div className={classNames(styles.revealingCaseSection__content, {
        [styles.revealingCaseSection__content_leftSide]: data.imageSide === 'left',
      })}
      >
        <div
          ref={listRef}
          className={styles.revealingCaseSection__contentList}
        >
          {data.revealingPoints.map((point, index) => (
            <React.Fragment key={point.id}>
              <RevealingCasePoint
                key={point.id}
                point={point}
                isActive={point.id === activePoint.id}
                handleClick={() => {
                  setActivePoint(point);
                  setActivePointIndex(index);
                }}
              />
              {index === activePointIndex && (
                <PointMedia
                  isClosedCase={data.case?.isClosed}
                  hasCase={!!data.case}
                  caseSlug={data.case?.slug}
                  isVideo={isVideo}
                  media={activeMedia}
                  pointsImage={data.image}
                  ref={mobileMediaRef}
                  mobile
                  key={`${point.id}_point_image`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <PointMedia
          isClosedCase={data.case?.isClosed}
          hasCase={!!data.case}
          caseSlug={data.case?.slug}
          isVideo={isVideo}
          media={activeMedia}
          pointsImage={data.image}
          ref={imageLinkRef}
        />
      </div>
    </section>
  );
};

export default RevealingCaseSection;
